import { z } from 'zod'
import { nullableArray, OptionalString, toInterval, WarningSchema } from '@/shared/api'
import type { Interval } from '@/shared/lib'

export const EXCURSION_STATES = ['planned', 'held', 'cancelled'] as const
export type ExcursionState = (typeof EXCURSION_STATES)[number]

export const BookingSchema = z
  .object({
    id: z.uuid(),
    client_id: z.uuid(),
    client_name: z.string().optional().default(''),
    client_phone: z.string().optional().default(''),
    seats: z.number().int().positive(),
    /** `public` — записался сам по ссылке, `manual` — завела команда. */
    source: z.enum(['public', 'manual']),
    active: z.boolean(),
    note: z.string().optional().default(''),
    cancellation_reason: OptionalString,
    placed_at: z.iso.datetime(),
  })
  .transform((dto) => ({
    id: dto.id,
    clientId: dto.client_id,
    clientName: dto.client_name,
    clientPhone: dto.client_phone,
    seats: dto.seats,
    source: dto.source,
    active: dto.active,
    note: dto.note,
    cancellationReason: dto.cancellation_reason,
    placedAt: dto.placed_at,
  }))

export type Booking = z.infer<typeof BookingSchema>

/**
 * Выход (`excursion.Excursion`). Время обязательно — выхода без времени не
 * существует. Занятость не хранится числом, считается по действующим записям;
 * вместимость — мягкий ориентир, перебор допустим (предупреждение).
 *
 * Секрет ссылки наружу не отдаётся: бэкенд собирает готовый `public_url` сам.
 */
export const ExcursionSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    starts_at: z.iso.datetime(),
    ends_at: z.iso.datetime(),
    meeting_point: OptionalString,
    guide_ids: nullableArray(z.uuid()),
    capacity: z.number().int().positive().nullish(),
    state: z.enum(EXCURSION_STATES),
    seats_taken: z.number().int().nonnegative(),
    active_bookings: z.number().int().nonnegative().default(0),
    cancellation_reason: OptionalString,
    public_url: OptionalString,
    bookings: z.array(BookingSchema).optional(),
  })
  .transform((dto) => ({
    id: dto.id,
    title: dto.title,
    slot: toInterval({ starts_at: dto.starts_at, ends_at: dto.ends_at }),
    meetingPoint: dto.meeting_point,
    guideIds: dto.guide_ids,
    capacity: dto.capacity ?? null,
    state: dto.state,
    seatsTaken: dto.seats_taken,
    activeBookings: dto.active_bookings,
    cancellationReason: dto.cancellation_reason,
    publicUrl: dto.public_url,
    bookings: dto.bookings,
  }))

export interface Excursion {
  id: string
  title: string
  slot: Interval
  meetingPoint: string | null
  guideIds: string[]
  capacity: number | null
  state: ExcursionState
  seatsTaken: number
  activeBookings: number
  cancellationReason: string | null
  publicUrl: string | null
  // exactOptionalPropertyTypes: поле реально отсутствует без with_bookings.
  bookings?: Booking[] | undefined
}

export const ExcursionListSchema = nullableArray(ExcursionSchema)

export const ExcursionResultSchema = z.object({
  excursion: ExcursionSchema,
  /** Занятые гиды — та же плоская форма, что и накладки у дел. */
  warnings: nullableArray(WarningSchema),
})

export const ClientContactSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    phone: z.string(),
  })
  .transform((dto) => ({ clientId: dto.id, name: dto.name, phone: dto.phone }))

export type ClientContact = z.infer<typeof ClientContactSchema>

export const CancelResultSchema = z
  .object({
    excursion: ExcursionSchema,
    /** Кому звонить: продукт клиентам не пишет — это делает человек. */
    clients: nullableArray(ClientContactSchema),
  })
  .transform((dto) => ({ excursion: dto.excursion, clientsToCall: dto.clients }))

export interface CancelResult {
  excursion: Excursion
  clientsToCall: ClientContact[]
}

export const BookingResultSchema = z
  .object({
    excursion: ExcursionSchema,
    booking_id: z.uuid(),
    /** Перебор вместимости допустим — это предупреждение, а не отказ. */
    overbooked: z.boolean().default(false),
  })
  .transform((dto) => ({
    excursion: dto.excursion,
    bookingId: dto.booking_id,
    overbooked: dto.overbooked,
  }))

export interface ExcursionDraft {
  title: string
  slot: Interval
  meetingPoint: string | null
  guideIds: string[]
  capacity: number | null
}

/**
 * Tri-state: поле отсутствует — не трогать, `null` — снять значение.
 *
 * У `slot` трёхзначности нет намеренно: время — инвариант агрегата, именно оно
 * делает выход выходом, а не замыслом. Снять его нельзя, только заменить —
 * в отличие от дела, где снятие времени уводит его из сетки в список.
 */
export interface ExcursionPatch {
  title?: string
  slot?: Interval
  meetingPoint?: string | null
  guideIds?: string[]
  capacity?: number | null
}

export function activeBookings(excursion: Excursion): Booking[] {
  return (excursion.bookings ?? []).filter((b) => b.active)
}
